import { useEffect, useRef } from "react";

// Interactive particle sphere rendered with Canvas2D (no external deps).
// Projects 3D points onto 2D, rotates slowly, and reacts to mouse.
export default function HeroCanvas() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = 0, height = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let visible = true;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width; height = rect.height;
      canvas.width = width * dpr; canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Pause animation when canvas is off-screen
    const visObs = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    visObs.observe(canvas);

    // Generate Fibonacci-sphere points (reduced from 1100 → 800)
    const N = 800;
    const points = [];
    for (let i = 0; i < N; i++) {
      const phi = Math.acos(1 - 2 * (i + 0.5) / N);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      points.push({
        x: Math.sin(phi) * Math.cos(theta),
        y: Math.sin(phi) * Math.sin(theta),
        z: Math.cos(phi),
      });
    }

    // Pre-allocate projection array — no per-frame allocations
    const proj = new Array(N);
    for (let i = 0; i < N; i++) proj[i] = { sx: 0, sy: 0, z: 0 };

    let rotX = 0, rotY = 0;
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - r.left) / r.width - 0.5;
      mouseRef.current.y = (e.clientY - r.top) / r.height - 0.5;
      mouseRef.current.active = true;
    };
    const onLeave = () => { mouseRef.current.active = false; };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);

    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      if (!visible) return;

      ctx.clearRect(0, 0, width, height);
      const cx = width / 2;
      const cy = height / 2;
      const R = Math.min(width, height) * 0.42;

      const target = mouseRef.current;
      rotY += 0.0016 + (target.active ? target.x * 0.02 : 0);
      rotX += 0.0008 + (target.active ? target.y * 0.02 : 0);

      const sinX = Math.sin(rotX), cosX = Math.cos(rotX);
      const sinY = Math.sin(rotY), cosY = Math.cos(rotY);

      // Project all points into pre-allocated proj array (zero allocations)
      for (let i = 0; i < N; i++) {
        const p = points[i];
        const pulse = 1 + Math.sin(t * 0.0006 + i * 0.02) * 0.012;
        let x = p.x * pulse, y = p.y * pulse, z = p.z * pulse;

        const xz = cosY * x + sinY * z;
        z = -sinY * x + cosY * z; x = xz;
        const yz = cosX * y - sinX * z;
        z = sinX * y + cosX * z;  y = yz;

        const persp = 600 / (600 - z * R * 1.6);
        let sx = cx + x * R * persp;
        let sy = cy + y * R * persp;

        if (target.active) {
          const mxp = cx + target.x * width * 0.4;
          const myp = cy + target.y * height * 0.4;
          const dx = sx - mxp, dy = sy - myp;
          const d2 = dx * dx + dy * dy;
          const r2 = 130 * 130;
          if (d2 < r2) {
            const d = Math.sqrt(d2) || 1;
            const push = (130 - d) / 130;
            sx += (dx / d) * push * 26;
            sy += (dy / d) * push * 26;
          }
        }
        proj[i].sx = sx; proj[i].sy = sy; proj[i].z = z;
      }

      // BATCH lines — single path, single stroke() call (was: ~200 individual strokes)
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = "rgba(200,200,210,0.18)";
      ctx.beginPath();
      for (let i = 0; i < N; i += 3) {
        const a = proj[i];
        for (let j = i + 1; j < Math.min(i + 5, N); j++) {
          const b = proj[j];
          const dx = a.sx - b.sx, dy = a.sy - b.sy;
          if (dx * dx + dy * dy < 55 * 55) {
            ctx.moveTo(a.sx, a.sy);
            ctx.lineTo(b.sx, b.sy);
          }
        }
      }
      ctx.stroke();

      // BATCH normal points — 4 depth buckets, 4 fill() calls total (was: ~770 individual fills)
      for (let bucket = 0; bucket < 4; bucket++) {
        const lo = bucket / 4, hi = (bucket + 1) / 4, mid = (lo + hi) / 2;
        const alpha = (0.15 + mid * 0.7).toFixed(2);
        const sz = 0.6 + mid * 2.2;
        ctx.fillStyle = `rgba(230,230,240,${alpha})`;
        ctx.beginPath();
        for (let i = 0; i < N; i++) {
          if (i % 37 !== 0) {
            const depth = (proj[i].z + 1) / 2;
            if (depth >= lo && depth < hi) {
              ctx.moveTo(proj[i].sx + sz, proj[i].sy);
              ctx.arc(proj[i].sx, proj[i].sy, sz, 0, Math.PI * 2);
            }
          }
        }
        ctx.fill();
      }

      // BATCH accent points — 1 fill() call (was: ~21 individual fills)
      ctx.fillStyle = "rgba(255,59,0,0.8)";
      ctx.beginPath();
      for (let i = 0; i < N; i += 37) {
        const depth = (proj[i].z + 1) / 2;
        const sz = 0.6 + depth * 2.2;
        ctx.moveTo(proj[i].sx + sz, proj[i].sy);
        ctx.arc(proj[i].sx, proj[i].sy, sz, 0, Math.PI * 2);
      }
      ctx.fill();
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      visObs.disconnect();
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ display: "block" }}
    />
  );
}
